import React, { useRef, useEffect } from 'react';
import {
  Chart as ChartJS, Tooltip, Legend, Title, CategoryScale, LinearScale,
} from 'chart.js';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';

ChartJS.register(TreemapController, TreemapElement, Tooltip, Legend, Title, CategoryScale, LinearScale);

const PALETTE = [
  '#065F46', '#047857', '#059669', '#10B981', '#14B8A6',
  '#0891B2', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6',
  '#A855F7', '#EC4899', '#F59E0B', '#EF4444',
];

/**
 * Treemap of revenue aggregated by project_type.
 * Uses chartjs-chart-treemap — each rectangle area is proportional to the
 * total revenue of its type, labeled with the type name and value.
 */
export default function RevenueByTypeTreemap({ data }) {
  const ref = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const dataset = (data || []).map((d, i) => ({
      ...d,
      _color: PALETTE[i % PALETTE.length],
    }));

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    chartRef.current = new ChartJS(ref.current, {
      type: 'treemap',
      data: {
        datasets: [{
          tree: dataset,
          key: 'total',
          labels: {
            display: true,
            color: '#fff',
            font: { weight: 'bold', size: 13 },
            formatter: (ctx) => {
              const item = ctx.raw?._data;
              if (!item) return '';
              const label = item.project_type || '';
              const total = item.total || 0;
              const currency = new Intl.NumberFormat('pt-BR', {
                style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
              }).format(total);
              return [label, currency];
            },
            align: 'center',
            position: 'middle',
            overflow: 'hidden',
          },
          backgroundColor: (ctx) => {
            const item = ctx.raw?._data;
            if (!item) return '#94A3B8';
            const idx = dataset.findIndex(x => x.project_type === item.project_type);
            return PALETTE[(idx >= 0 ? idx : 0) % PALETTE.length];
          },
          borderColor: '#fff',
          borderWidth: 2,
          spacing: 1,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => items[0]?.raw?._data?.project_type || '',
              label: (ctx) => {
                const v = ctx.raw?._data?.total || 0;
                const fmt = new Intl.NumberFormat('pt-BR', {
                  style: 'currency', currency: 'BRL',
                }).format(v);
                return `Total: ${fmt}`;
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-gray-500" data-testid="treemap-empty">
        Sem receitas para exibir.
      </div>
    );
  }

  return (
    <div className="h-64" data-testid="revenue-by-type-treemap">
      <canvas ref={ref} />
    </div>
  );
}
